import { useState, useEffect, createContext, useContext } from 'react'
import { toast } from 'react-toastify'
import api from '../services/api'
import { clearSelectedClientId } from '../services/clientFilterStore'

const AuthContext = createContext(null)

export const AuthProvider = ({ children, queryClient }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [isAuthenticated, setIsAuthenticated] = useState(!!token)

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Token ${token}`
      fetchUser()
    }
    // fetchUser se redefine en cada render: incluirla haria que el efecto
    // corriera siempre -> setUser -> render -> efecto. Bucle infinito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Invalidar todas las queries cuando cambia el usuario
  useEffect(() => {
    if (user && queryClient) {
      // Limpiar el caché para forzar la recarga cuando cambia el usuario
      queryClient.clear()
      // Invalidar todas las queries
      queryClient.invalidateQueries()
    }
    // Depende de user?.id a proposito: con `user` limpiaria el cache de queries
    // cada vez que cambia la identidad del objeto, no solo al cambiar de usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, queryClient])

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/users/me/')
      setUser(response.data)
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Error fetching user:', error)
      logout()
    }
  }

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/users/login/', {
        username,
        password,
      })
      const { token, user } = response.data
      
      // Limpiar el caché antes de establecer el nuevo usuario
      if (queryClient) {
        queryClient.clear()
        queryClient.invalidateQueries()
      }
      
      setToken(token)
      setUser(user)
      setIsAuthenticated(true)
      localStorage.setItem('token', token)
      api.defaults.headers.common['Authorization'] = `Token ${token}`
      toast.success('Sesión iniciada correctamente')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Error al iniciar sesión'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const logout = () => {
    api.post('/auth/users/logout/').catch(() => {})
    
    // Limpiar todo el caché de React Query
    if (queryClient) {
      queryClient.clear()
      queryClient.invalidateQueries()
    }
    
    setToken(null)
    setUser(null)
    setIsAuthenticated(false)
    localStorage.removeItem('token')
    clearSelectedClientId()
    delete api.defaults.headers.common['Authorization']
    toast.info('Sesión cerrada')
  }

  // Verificar si el usuario es superusuario
  const isSuperuser = user?.is_superuser || false
  const isStaff = user?.is_staff || false

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      isAuthenticated, 
      isSuperuser,
      isStaff,
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

